import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { computed, Injectable } from '@angular/core';
import { catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { apiUrl } from '../../config/api-url';
import { ErrorhandlerService } from './errorhandler.service';
import {
  requestActivateFactorPlayload,
  requestBypassCodePayload,
  requestChallengePayload,
  requestEnrollFactorPlayload,
  requestforEmulationPayload,
  requestGetUserPayload,
  requestPayload,
  requestRemoveUserFactorPayload,
  requestVerifyFactorPlayload,
  reqVerifyMfaStatusPayload,
} from '../models/model';
import config from '../../../assets/config.json';
import { IncognitoService } from './incongito-helper';
import { Router } from '@angular/router';
import { switchAdvisorService } from './switchAdvisorService';

@Injectable({
  providedIn: 'root',
})
export class OktaService {
  private readonly apiUrl = environment.apiHost; // Use HTTPS and secure URLs
  private readonly endPoints = apiUrl;
  public userData: any;
  private MFALoginKeyName = config.MFALoginKeyName; // Key for storing MFA login token
  configData: any = config;
  switchedAdvisor = computed(() => this._switchAdvisor.switchedAdvisor());
  constructor(
    private _http: HttpClient,
    private _errorHandlerService: ErrorhandlerService,
    private _incognitoDetectionService: IncognitoService,
    private _router: Router,
    private _switchAdvisor: switchAdvisorService,
  ) {}
  getSelectFactor(body: requestPayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.selectUserFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.handleError(error)), // Use the centralized error handler
    );
  }
  enrollFactorApi(body: requestEnrollFactorPlayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.enrollUserFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }

  activateFactorFactorApi(body: requestActivateFactorPlayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.activateFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }

  challengeFactorApi(body: requestChallengePayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.challengeFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }
  verifyFactorApi(body: requestVerifyFactorPlayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.verifyFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }
  getUserFactor(body: requestPayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.getUserFactors, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }
  resendFactor(body: requestEnrollFactorPlayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.resendfactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }
  getUser(body: requestGetUserPayload): Observable<any> {
    const rememberMeToken = this.getRememberMeTokenFromCookie();
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        MfaRememberMe: rememberMeToken ? rememberMeToken : '',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.getUserDetails, body, httpHeaderOptions).pipe(
      // eslint-disable-next-line no-return-assign
      tap((response: any) => (this.userData = response)), // Save the response data to the variable
      catchError((error: HttpErrorResponse) => this._errorHandlerService.handleError(error)), // Use the centralized error handler
    );
  }
  getUserProfile(body: requestforEmulationPayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ...(this.switchedAdvisor() ? { AdvisorUserId: String(this.switchedAdvisor()?.advisorId) } : {}),
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.getUserProfile, body, httpHeaderOptions).pipe(
      // eslint-disable-next-line no-return-assign
      tap((response: any) => (this.userData = response)), // Save the response data to the variable
      catchError((error: HttpErrorResponse) => this._errorHandlerService.handleError(error)), // Use the centralized error handler
    );
  }
  removeUserFactor(body: requestRemoveUserFactorPayload): Observable<any> {
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.removeUserFactor, body, httpHeaderOptions).pipe(
      catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)), // Use the centralized error handler
    );
  }

  getFactorValue(userInfo: any, type: number) {
    return Array.isArray(userInfo.factors)
      ? userInfo.factors.filter((d: any) => d.factorType === type && d.status === 1)[0]?.factorValue
        ? userInfo.factors.filter((d: any) => d.factorType === type && d.status === 1)[0]?.factorValue
        : ''
      : '';
  }

  validateByPassCode(body: requestBypassCodePayload): Observable<any> {
    return new Observable((observer) => {
      const httpHeaderOptions: { headers: HttpHeaders } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      };
      this._http
        .post<any>(this.apiUrl + this.endPoints.validateBypasscode, body, httpHeaderOptions)
        .pipe(catchError((error: HttpErrorResponse) => this._errorHandlerService.getErrorDetails(error)))
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
    });
  }

  //Api call to verify MFA status
  verifyMFAStatus(body: reqVerifyMfaStatusPayload): Observable<any> {
    const verifyMfaToken = this.getMFAToken();
    const httpHeaderOptions: { headers: HttpHeaders } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        zxp93sn: verifyMfaToken ? verifyMfaToken : '',
      }),
    };
    return this._http.post<any>(this.apiUrl + this.endPoints.verifyMfaStatus, body, httpHeaderOptions).pipe(
      // eslint-disable-next-line no-return-assign
      tap((response: any) => (this.userData = response)), // Save the response data to the variable
      catchError((error: HttpErrorResponse) => this._errorHandlerService.handleError(error)), // Use the centralized error handler
    );
  }

  getMFAToken(): string | null {
    const cookieName = this.MFALoginKeyName;
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(`${cookieName}=`)) {
        try {
          const token = atob(cookie.substring(cookieName.length + 1));
          if (token.endsWith(this.MFALoginKeyName)) {
            return token.substring(0, token.length - this.MFALoginKeyName.length); // Decode the token
          }
          return atob(cookie.substring(cookieName.length + 1));
        } catch (e) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          this._router.navigate(['/advisor/mfa']);
        }
      }
    }
    return null;
  }

  // Retrieve the token from cookie
  getRememberMeTokenFromCookie(): string | null {
    const cookieName = 'rememberMeToken';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(`${cookieName}=`)) {
        try {
          return atob(cookie.substring(cookieName.length + 1));
        } catch (e) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          this._router.navigate(['/advisor/mfa']);
        }
      }
    }
    return null;
  }

  /**
   * Returns the browser name based on the user agent string.
   */
  getBrowserName(): string {
    return this._incognitoDetectionService.getBrowserName();
  }
}
